import React from 'react';
import { Sparkles, Lock, Download } from 'lucide-react';
import { createGlassCard, createButton, sharedStyles } from './shared/sharedStyles.js';
import GuestReportCard from './GuestReportCard.jsx';

const GuestReportTable = ({
  guestProfiles,
  copiedIndex,
  onCopyEmail,
  onGeneratePitch,
  hasPremiumAccess = false,
  showTitle = ''
}) => {
  const handleExportCSV = () => {
    // Prepare CSV headers (using snake_case for data hygiene)
    const headers = [
      'name',
      'title',
      'company',
      'relevance_score',
      'summary',
      'expertise_areas',
      'report_date'
    ];

    // Add premium fields if user has access
    if (hasPremiumAccess) {
      headers.push('email', 'linkedin', 'twitter', 'pitch');
    }

    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Convert guest data to CSV rows
    const rows = guestProfiles.map(guest => {
      // Format timestamp as human-readable date
      const discoveredDate = guest.discoveredAt
        ? new Date(guest.discoveredAt).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short'
        })
        : '';

      const row = [
        escapeCSV(guest.name),
        escapeCSV(guest.title),
        escapeCSV(guest.company),
        escapeCSV((guest.relevanceScore * 10).toFixed(1)),
        escapeCSV(guest.summary),
        escapeCSV(guest.expertiseAreas?.join('; ') || ''),
        escapeCSV(discoveredDate)
      ];

      // Add premium fields if user has access
      if (hasPremiumAccess) {
        row.push(
          escapeCSV(guest.socialMedia?.email || ''),
          escapeCSV(guest.socialMedia?.linkedin || ''),
          escapeCSV(guest.socialMedia?.twitter || ''),
          escapeCSV(guest.pitch || '')
        );
      }

      return row.join(',');
    });

    // Combine headers and rows
    const csv = [headers.join(','), ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate filename with show title and date
    const date = new Date().toISOString().split('T')[0];
    const filename = `${showTitle ? showTitle.replace(/[^a-z0-9]/gi, '_') + '_' : ''}guest_report_${date}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      flex: 1,
      ...createGlassCard(),
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'fit-content'
    }}>
      {/* Panel Header */}
      <div style={{
        padding: '1rem 1.25rem',
        borderBottom: `1px solid ${sharedStyles.colors.primary}20`,
        background: sharedStyles.colors.surfaceAlt
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: sharedStyles.colors.textPrimary,
              margin: 0
            }}>
              Guest Report
            </h2>
            <Sparkles size={18} style={{ color: sharedStyles.colors.primary }} />
            <span style={{
              color: sharedStyles.colors.textSecondary,
              fontSize: '0.8rem',
              marginLeft: '0.5rem'
            }}>
              AI-matched guests based on your show profile
            </span>
          </div>

          {/* Export CSV Button */}
          <button
            onClick={handleExportCSV}
            style={{
              ...createButton('outline'),
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Guest Table */}
      <div style={{
        flex: 1,
        overflowY: 'auto'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '20% 8% 1fr 12% 10%',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid rgba(234, 88, 12, 0.1)',
          background: 'rgba(234, 88, 12, 0.02)',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase'
          }}>
            Name
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase',
            textAlign: 'center'
          }}>
            Match
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase'
          }}>
            Summary
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: hasPremiumAccess ? sharedStyles.colors.textSecondary : sharedStyles.colors.textTertiary,
            textTransform: 'uppercase',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem'
          }}>
            {!hasPremiumAccess && <Lock size={10} />}
            Socials
          </div>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: hasPremiumAccess ? sharedStyles.colors.textSecondary : sharedStyles.colors.textTertiary,
            textTransform: 'uppercase',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.25rem'
          }}>
            {!hasPremiumAccess && <Lock size={10} />}
            Pitch
          </div>
        </div>

        {/* Guest Rows */}
        {guestProfiles.map((guest, index) => (
          <GuestReportCard
            key={guest.id}
            guest={guest}
            index={index}
            copiedIndex={copiedIndex}
            onCopyEmail={onCopyEmail}
            onGeneratePitch={onGeneratePitch}
            hasPremiumAccess={hasPremiumAccess}
          />
        ))}
      </div>
    </div>
  );
};

export default GuestReportTable;